
import React, { type JSX } from 'react';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import Quill, { type EmitterSource } from 'quill';
import TurndownService from 'turndown';
import { marked } from 'marked';
import type { Delta as DeltaType } from 'quill/core';
import Delta from 'quill-delta';
import QuillResizeImage from '@components/quill-resize-image/main';
import QuillSyntax from 'quill/modules/syntax';
import TableUp, { defaultCustomSelect, TableAlign, TableMenuContextmenu, TableResizeBox, TableResizeScale, TableSelection, TableVirtualScrollbar } from 'quill-table-up';
import ImageFullscreen from './ImageFullscreen';
import { Skeleton } from './Skeleton';
import 'quill-table-up/index.css';
import 'quill-table-up/table-creator.css';

Quill.register('modules/resize', QuillResizeImage);
Quill.register('modules/syntax', QuillSyntax);
Quill.register({ [`modules/${TableUp.moduleName}`]: TableUp }, true);

import { SizeClass } from 'quill/formats/size';
import { PromiseCompletion } from '../classes/PromiseCompletion';
import { socketClientService } from '../services/SocketClientService';
import { apiService, HttpError } from '../services/ApiService';
import { onlineService } from '../services/OnlineService';
import { notificationService } from '../services/NotificationService';

const icons = Quill.import("ui/icons") as Partial<Record<string, string>>;
icons["undo"] = 'UNDO';

//https://github.com/quilljs/quill/issues/2052

export type EditorProps = {
  noteId: string;
  sharedView: boolean;
  notFoundView?: React.ComponentType<any>;
  accessDeniedView?: React.ComponentType<any>;
  onDataLoaded?: (data: NoteResponse) => void;
};

type IQuillHistoryModule = {
  history?: {
    clear: () => void;
  }
}

type NoteEvent = {
  delta: string;
}

export type NoteResponse = {
  name: string;
  content: string;
  sharedAsReadOnly: boolean;
};

(SizeClass as any).whitelist = ['10px', 'large', 'huge', '4.242px'];

@observer
export default class Editor extends React.Component<EditorProps> {
  private _changes = new Delta();
  private _isUpdatingFromServer = false;
  private _editorContainerRef: React.RefObject<HTMLDivElement> = React.createRef();
  private _quillInstance: Quill | null = null;
  private _loader: PromiseCompletion = new PromiseCompletion();
  private _saveInterval = 0;
  @observable private _isLoaded = false;
  private _initialContent: string = '';
  @observable private _canEdit = false;
  @observable private _notFound = false;
  @observable private _accessDenied = false;
  @observable private _isPainting = false;
  @observable.ref private _fullscreenImages: string[] | null = null;
  @observable private _fullscreenStartIndex = 0;
  private _savingLoader = new PromiseCompletion();
  private _uploadLoader = new PromiseCompletion();
  private _savedFormats: Record<string, unknown> | null = null;
  private _codeBlockObserver: MutationObserver | null = null;
  private _floatingCopyBtn: HTMLButtonElement | null = null;
  private _floatingCopyTarget: HTMLElement | null = null;
  private _imageResizeRaf = 0;
  private _imageResizeThrottle = 0;
  private _tableLayoutObserver: ResizeObserver | null = null;
  private _tableSyncThrottle = 0;
  // True while rewriting <img> src so Quill's MutationObserver doesn't flag it as a user edit.
  private _isResizingImages = false;
  private _toolbarMoreOutsideHandler: ((e: MouseEvent) => void) | null = null;
  // Which toolbar layout the current Quill instance was built for; crossing the
  // 860px breakpoint rebuilds Quill with the other layout (see _rebuildQuillForLayout).
  private _toolbarMode: 'mobile' | 'desktop' | null = null;

  constructor(props: EditorProps){
    super(props);
    makeObservable(this);
  }

  async componentDidMount(): Promise<void>{
    const { noteId } = this.props;
    socketClientService.subscribe<NoteEvent>('note', noteId, 'note-update', this._onNoteUpdate);
    socketClientService.on('access-denied', this._onSocketAccessDenied);
    socketClientService.on('not-found', this._onSocketNotFound);
    socketClientService.on('save-error', this._onSocketSaveError);
    window.addEventListener('beforeunload', this._onBeforeUnload);
    window.addEventListener('resize', this._onWindowResize);
    // Delegated image-load listener (capture) so we can size an image the moment
    // its dimensions become known, even for late-loading images.
    document.addEventListener('load', this._onImageLoad, true);
    this._saveInterval = window.setInterval(this._saveCurrentChanges, 500);
    await this._loadNote(noteId);
  }

  componentDidUpdate(_prevProps: EditorProps): void {
    // Reinitialize Quill if canEdit state changed and container is available
    if (this._editorContainerRef.current && !this._quillInstance) {
      this._initializeQuill();
    }
  }

  componentWillUnmount(): void {
    const { noteId } = this.props;
    socketClientService.unsubscribe('note-update', noteId, this._onNoteUpdate);
    socketClientService.off('access-denied', this._onSocketAccessDenied);
    socketClientService.off('not-found', this._onSocketNotFound);
    socketClientService.off('save-error', this._onSocketSaveError);
    window.removeEventListener('beforeunload', this._onBeforeUnload);
    window.removeEventListener('resize', this._onWindowResize);
    document.removeEventListener('load', this._onImageLoad, true);
    if (this._imageResizeRaf) {
      cancelAnimationFrame(this._imageResizeRaf);
      this._imageResizeRaf = 0;
    }
    if (this._imageResizeThrottle) {
      clearTimeout(this._imageResizeThrottle);
      this._imageResizeThrottle = 0;
    }
    if (this._tableLayoutObserver) {
      this._tableLayoutObserver.disconnect();
      this._tableLayoutObserver = null;
    }
    if (this._tableSyncThrottle) {
      clearTimeout(this._tableSyncThrottle);
      this._tableSyncThrottle = 0;
    }
    window.clearInterval(this._saveInterval);
    this._saveInterval = 0;
    this._saveCurrentChanges();
    if (this._quillInstance) {
      this._quillInstance.off('selection-change', this._onSelectionChange);
      this._quillInstance.off('text-change', this._onTextChange);
      this._quillInstance = null;
    }
    this._codeBlockObserver?.disconnect();
    this._codeBlockObserver = null;
    this._floatingCopyBtn?.remove();
    this._floatingCopyBtn = null;
    this._floatingCopyTarget = null;
    if (this._toolbarMoreOutsideHandler) {
      document.removeEventListener('click', this._toolbarMoreOutsideHandler);
      this._toolbarMoreOutsideHandler = null;
    }
  }

  render(): JSX.Element{
    const { notFoundView, accessDeniedView } = this.props;

    if (this._notFound && notFoundView){
      return React.createElement(notFoundView);
    }
    if (this._accessDenied && accessDeniedView){
      return React.createElement(accessDeniedView);
    }

    if (!this._isLoaded){
      return (
        <div className="note-details">
          <div className="editor-skeleton">
            <Skeleton height={44} borderRadius={0} />
            <div className="editor-skeleton-body">
              <Skeleton height={22} width="40%" />
              <Skeleton height={14} width="100%" />
              <Skeleton height={14} width="96%" />
              <Skeleton height={14} width="90%" />
              <Skeleton height={14} width="94%" />
              <Skeleton height={14} width="62%" />
            </div>
          </div>
        </div>
      );
    }

    let className = 'note-details';
    if (!this._canEdit){
      className += ' note-details-readonly';
    }
    if (this._isPainting){
      className += ' format-painter-active';
    }

    const showSavingIndicator = this._canEdit && (this._savingLoader.isPending || this._uploadLoader.isPending);

    return (
      <div className={className}>
        <div id="snow-container">
          <div ref={this._editorContainerRef} onKeyDown={this._onEditorKeyDown} />
        </div>
        {showSavingIndicator && (
          <div className="editor-saving-indicator">
            <span className="editor-saving-spinner" />
            {this._uploadLoader.isPending ? 'Uploading…' : 'Saving…'}
          </div>
        )}
        {this._fullscreenImages && (
          <ImageFullscreen
            images={this._fullscreenImages}
            startIndex={this._fullscreenStartIndex}
            onClose={() => this._closeImageFullscreen()}
          />
        )}
      </div>
    );
  }

  private _openImageFullscreen(img: HTMLImageElement): void {
    if (!this._quillInstance) return;
    // Fullscreen always shows the full-size original — strip the inline `?w=`
    // sizing param from every src (view, preload and download all use these).
    const src = img.getAttribute('src') ? this._stripImageSizeParam(img.getAttribute('src') as string) : null;
    const images = Array.from(this._quillInstance.root.querySelectorAll('img'))
      .map(el => el.getAttribute('src'))
      .filter((s): s is string => !!s)
      .map(s => this._stripImageSizeParam(s));
    if (images.length === 0) return;
    let index = src ? images.indexOf(src) : 0;
    if (index === -1) index = 0;
    this._fullscreenImages = images;
    this._fullscreenStartIndex = index;
  }

  private _closeImageFullscreen(): void {
    this._fullscreenImages = null;
    this._fullscreenStartIndex = 0;
  }

  private async _loadNote(activeNoteId: string): Promise<void>{
    runInAction(() => {
      this._notFound = false;
      this._accessDenied = false;
    });
    try{

      const data = await apiService.get<NoteResponse>('/api/note/' + activeNoteId, null, {
        loader: this._loader,
        defaultErrorHandling: false
      });

      let canEdit = !data.sharedAsReadOnly;
      if (data.sharedAsReadOnly && !this.props.sharedView){
        canEdit = true;
      }

      runInAction(() => {
        this._canEdit = canEdit;
        this._initialContent = data.content;
        this._isLoaded = true;
      });
      this.props.onDataLoaded?.(data);

      if (!this.props.sharedView && onlineService.isOnline) {
        apiService.post('/api/note/' + activeNoteId + '/current', {}, { defaultErrorHandling: false }).catch(() => { /* non-fatal */ });
      }

      // If Quill was already initialized before data loaded (race with parent re-render),
      // apply content directly now instead of waiting for the next componentDidUpdate.
      if (this._quillInstance) {
        this._updateFromData(this._initialContent, false);
        this._initialContent = '';
      }

    } catch (error) {
      const httpError = error as HttpError;
      if (httpError?.code === 404){
        runInAction(() => { this._notFound = true; });
      } else if (httpError?.code === 403){
        runInAction(() => { this._accessDenied = true; });
      } else {
        apiService.defaultErrorHandler(error as HttpError);
      }
    }
  }

  private _initializeQuill(): void {
    if (!this._editorContainerRef.current || this._quillInstance) return;

    const icons = Quill.import("ui/icons") as Partial<Record<string, string>>;
    icons["undo"] = `<?xml version="1.0"?>
<svg width="18px" height="18px" viewBox="10 10 66 66" fill="none" xmlns="http://www.w3.org/2000/svg">
<path class="ql-fill ql-stroke" fill-opacity="1" stroke-width="0.2" stroke-linejoin="round" d="M 52.7514,25.076C 53.4111,25.7534 54.0258,26.4933 54.5953,27.296C 55.1649,28.0987 55.6564,28.9607 56.0698,29.8821C 56.4832,30.8035 56.8065,31.7766 57.0396,32.8014C 57.2727,33.8262 57.3892,34.8883 57.3892,35.9878C 57.3892,37.0566 57.276,38.1517 57.0495,39.2733C 56.823,40.3948 56.4491,41.5394 55.928,42.7071C 55.4068,43.8748 54.713,45.07 53.8465,46.2927C 52.9801,47.5154 51.9069,48.7601 50.6271,50.0267C 50.2137,50.4358 49.7431,50.902 49.2153,51.4253C 48.6875,51.9487 48.1278,52.5029 47.5363,53.0878C 46.9447,53.6728 46.34,54.2742 45.722,54.8922C 45.1041,55.5101 44.4983,56.1072 43.9045,56.6833C 42.5323,58.0424 41.1029,59.4586 39.6163,60.9319L 35.3545,56.6239C 36.8235,55.1638 38.2441,53.7542 39.6163,52.3951C 40.1969,51.819 40.7994,51.2252 41.424,50.6139C 42.0485,50.0025 42.6565,49.4011 43.2481,48.8095C 43.8396,48.218 44.3993,47.6605 44.9271,47.1371C 45.4549,46.6138 45.9255,46.1476 46.3389,45.7385C 48.0146,44.0584 49.2758,42.3904 50.1224,40.7345C 50.969,39.0786 51.3924,37.4678 51.3924,35.9021C 51.3924,34.719 51.1516,33.5777 50.67,32.4781C 50.1884,31.3786 49.4528,30.3406 48.4632,29.3642C 48.2169,29.1179 47.9112,28.8848 47.5462,28.6649C 47.1811,28.445 46.7688,28.2526 46.3092,28.0877C 45.8496,27.9227 45.3482,27.7886 44.805,27.6852C 44.2619,27.5819 43.6868,27.5302 43.0799,27.5302C 42.4641,27.5302 41.8198,27.5929 41.1469,27.7182C 40.474,27.8436 39.7933,28.0503 39.105,28.3384C 38.4167,28.6264 37.7218,29.0036 37.0203,29.4698C 36.3188,29.936 35.6316,30.5056 34.9587,31.1785L 30.466,35.6976L 37.4986,42.75L 19,42.75L 19,24.5417L 25.9931,31.4094L 30.5056,26.8705C 31.5083,25.7446 32.5463,24.8275 33.6194,24.1194C 34.6926,23.4113 35.7646,22.8616 36.8356,22.4701C 37.9065,22.0787 38.972,21.8148 40.0319,21.6785C 41.0919,21.5421 42.1079,21.474 43.0799,21.474C 44.087,21.474 45.0579,21.563 45.9925,21.7411C 46.9271,21.9193 47.8068,22.17 48.6314,22.4932C 49.4561,22.8165 50.2148,23.198 50.9075,23.6378C 51.6002,24.0777 52.2148,24.5571 52.7514,25.076 Z " />
</svg>`
    icons["redo"] = `<?xml version="1.0"?>
<svg width="18px" height="18px" viewBox="10 10 66 66" fill="none" xmlns="http://www.w3.org/2000/svg">
<path class="ql-fill ql-stroke" fill-opacity="1" stroke-width="0.2" stroke-linejoin="round" d="M 23.2486,25.076C 22.5889,25.7534 21.9742,26.4933 21.4047,27.296C 20.8351,28.0987 20.3436,28.9607 19.9302,29.8821C 19.5168,30.8035 19.1935,31.7766 18.9604,32.8014C 18.7273,33.8262 18.6107,34.8883 18.6107,35.9878C 18.6107,37.0566 18.724,38.1517 18.9505,39.2733C 19.177,40.3948 19.5508,41.5394 20.072,42.7071C 20.5932,43.8748 21.287,45.07 22.1535,46.2927C 23.0199,47.5154 24.093,48.7601 25.3729,50.0267C 25.7863,50.4358 26.2569,50.902 26.7847,51.4253C 27.3125,51.9487 27.8721,52.5029 28.4637,53.0878C 29.0552,53.6728 29.66,54.2742 30.2779,54.8922C 30.8959,55.5101 31.5017,56.1072 32.0955,56.6833C 33.4677,58.0424 34.8971,59.4586 36.3837,60.9319L 40.6455,56.6239C 39.1765,55.1638 37.7559,53.7542 36.3837,52.3951C 35.8031,51.819 35.2006,51.2252 34.576,50.6139C 33.9515,50.0025 33.3434,49.4011 32.7519,48.8095C 32.1603,48.218 31.6007,47.6605 31.0729,47.1371C 30.5451,46.6138 30.0745,46.1476 29.6611,45.7385C 27.9854,44.0584 26.7242,42.3904 25.8776,40.7345C 25.0309,39.0786 24.6076,37.4678 24.6076,35.9021C 24.6076,34.719 24.8484,33.5777 25.33,32.4781C 25.8116,31.3786 26.5472,30.3406 27.5368,29.3642C 27.7831,29.1179 28.0887,28.8848 28.4538,28.6649C 28.8188,28.445 29.2312,28.2526 29.6908,28.0877C 30.1504,27.9227 30.6518,27.7886 31.1949,27.6852C 31.7381,27.5819 32.3132,27.5302 32.9201,27.5302C 33.5359,27.5302 34.1802,27.5929 34.8531,27.7182C 35.526,27.8436 36.2066,28.0503 36.8949,28.3384C 37.5833,28.6264 38.2782,29.0036 38.9797,29.4698C 39.6812,29.936 40.3684,30.5056 41.0413,31.1785L 45.534,35.6976L 38.5014,42.75L 57,42.75L 57,24.5417L 50.0069,31.4094L 45.4944,26.8705C 44.4916,25.7446 43.4537,24.8275 42.3805,24.1194C 41.3074,23.4113 40.2353,22.8616 39.1644,22.4701C 38.0934,22.0787 37.028,21.8148 35.968,21.6785C 34.9081,21.5421 33.8921,21.474 32.9201,21.474C 31.9129,21.474 30.942,21.563 30.0074,21.7411C 29.0728,21.9193 28.1932,22.17 27.3686,22.4932C 26.5439,22.8165 25.7852,23.198 25.0925,23.6378C 24.3998,24.0777 23.7852,24.5571 23.2486,25.076 Z " />
</svg>`;
    icons["format-painter"] = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-100 -100 1124 1124" width="18px" height="18px"><path d="M840 192h-56v-72c0-13.3-10.7-24-24-24H168c-13.3 0-24 10.7-24 24v272c0 13.3 10.7 24 24 24h592c13.3 0 24-10.7 24-24V256h32v200H465c-22.1 0-40 17.9-40 40v136h-44c-4.4 0-8 3.6-8 8v228c0 0.6 0.1 1.3 0.2 1.9-0.1 2-0.2 4.1-0.2 6.1 0 46.4 37.6 84 84 84s84-37.6 84-84c0-2.1-0.1-4.1-0.2-6.1 0.1-0.6 0.2-1.2 0.2-1.9V640c0-4.4-3.6-8-8-8h-44V520h351c22.1 0 40-17.9 40-40V232c0-22.1-17.9-40-40-40zM720 352H208V160h512v192zM477 876c0 11-9 20-20 20s-20-9-20-20V696h40v180z" class="ql-stroke ql-fill" fill="none" stroke-width="1.8"/></svg>`;
    icons["copy-markdown"] = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 28 24"><rect x="8" y="6" width="17" height="16" rx="2" class="ql-stroke" fill="none" stroke-width="2"/><path d="M4 18H3a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1" class="ql-stroke" fill="none" stroke-width="2"/><text x="9.5" y="17.5" font-size="7" font-weight="bold" font-family="monospace" class="ql-fill">MD</text></svg>`;
    icons["paste-markdown"] = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 28 24"><path d="M18 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" class="ql-stroke" fill="none" stroke-width="2"/><rect x="9" y="1" width="8" height="5" rx="1" class="ql-stroke" fill="none" stroke-width="2"/><text x="9" y="18" font-size="7" font-weight="bold" font-family="monospace" class="ql-fill">MD</text></svg>`;
    
    const modules: Record<string, any> = {
      toolbar: [],
      history: {
        delay: 1000,
        maxStack: 100,
        userOnly: true
      },
      syntax: {
        hljs: window.hljs,
        languages: [
          { key: 'plaintext', label: 'Plain Text' },
          { key: 'javascript', label: 'JavaScript' },
          { key: 'typescript', label: 'TypeScript' },
          { key: 'python', label: 'Python' },
          { key: 'java', label: 'Java' },
          { key: 'csharp', label: 'C#' },
          { key: 'cpp', label: 'C++' },
          { key: 'c', label: 'C' },
          { key: 'php', label: 'PHP' },
          { key: 'ruby', label: 'Ruby' },
          { key: 'go', label: 'Go' },
          { key: 'rust', label: 'Rust' },
          { key: 'swift', label: 'Swift' },
          { key: 'kotlin', label: 'Kotlin' },
          { key: 'sql', label: 'SQL' },
          { key: 'bash', label: 'Bash' },
          { key: 'shell', label: 'Shell' },
          { key: 'xml', label: 'XML/HTML' },
          { key: 'css', label: 'CSS' },
          { key: 'scss', label: 'SCSS' },
          { key: 'less', label: 'Less' },
          { key: 'json', label: 'JSON' },
          { key: 'yaml', label: 'YAML' },
          { key: 'markdown', label: 'Markdown' },
          { key: 'diff', label: 'Diff' },
        ],
        interval: 1000
      }
    };

    if (this._canEdit) {
      // On phones keep only high-frequency controls on the row; the rest go
      // into a "More" dropdown (see _layoutToolbar) to save vertical space.
      const isMobile = window.innerWidth <= 860;
      this._toolbarMode = isMobile ? 'mobile' : 'desktop';
      const toolbarContainer: any[] = this._getToolbarGroups(isMobile);
      modules.toolbar = {
        container: toolbarContainer,
        handlers: {
          'undo': () => {
            if (!this._quillInstance) return;
            (this._quillInstance as unknown as {history: {undo: () => void}}).history.undo();
          },
          'redo': () => {
            if (!this._quillInstance) return;
            (this._quillInstance as unknown as {history: {redo: () => void}}).history.redo();
          },
          'format-painter': () => {
            if (!this._quillInstance) return;
            const range = this._quillInstance.getSelection();
            if (range) {
              this._savedFormats = this._quillInstance.getFormat(range) as Record<string, unknown>;
              this._isPainting = true;
            }
          },
          'copy-markdown': () => { this._copyAsMarkdown(); },
          'paste-markdown': () => { this._pasteMarkdown(); },
        }
      };
      
      modules.uploader = {
        mimetypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp', 'image/tiff'],
        upload: (file: File): Promise<string> => this._uploadImage(file),
      };

      modules[TableUp.moduleName] = {
        customSelect: defaultCustomSelect,
        modules: [
          { module: TableVirtualScrollbar },
          { module: TableAlign },
          { module: TableResizeBox },
          { module: TableResizeScale },
          { module: TableSelection },
          { module: TableMenuContextmenu },
        ],
      };
    }

    // Image resize / fullscreen module is registered in both editable and
    // read-only (shared) modes. In the shared view `viewOnly` is true so the
    // plugin only wires up click-to-fullscreen (no hover resizer / handle).
    modules.resize = {
      locale: {},
      viewOnly: this.props.sharedView,
      onImageClick: (img: HTMLImageElement) => this._openImageFullscreen(img),
      onResizeEnd: () => this._onImageResizeEnd(),
    };

    this._quillInstance = new Quill(this._editorContainerRef.current, {
      theme: 'snow',
      modules,
      readOnly: !this._canEdit
    });

    this._quillInstance.on('text-change', this._onTextChange);
    this._quillInstance.on('selection-change', this._onSelectionChange);

    this._layoutToolbar();
    this._setupCodeBlockFeatures();
    this._setupSpoilerReveal();
    this._observeTableLayout();

    // Set initial content if available
    if (this._initialContent) {
      this._updateFromData(this._initialContent, false);
      this._initialContent = '';
    }
  }

  private _updateFromData(delta: string | DeltaType, update: boolean, source?: EmitterSource) {
    this._isUpdatingFromServer = true;
    try {
      let quillDelta: DeltaType;

      if (typeof delta === "string") {
        if (delta){
          quillDelta = JSON.parse(delta);
        } else {
          quillDelta = new Delta();
        }
      } else {
        quillDelta = delta as DeltaType;
      }

      if (update) {
        this._quillInstance?.updateContents(quillDelta, source);
      } else {
        //https://github.com/quilljs/quill/issues/1537
        this._quillInstance?.setText("");
        this._quillInstance?.setContents(quillDelta);
        (this._quillInstance as IQuillHistoryModule).history?.clear();
      }
    } finally {
      this._isUpdatingFromServer = false;
    }
    // Re-apply per-image server-side sizing once the DOM reflects the new content.
    this._scheduleApplyImageSizes();
  }

  // ── Server-side image sizing ───────────────────────────────────────────────
  // Inline images are stored at full resolution. To avoid downloading a huge
  // original when it only displays small, we request a `?w=`-scaled variant
  // whose width matches the displayed size (×2 for retina). The fullscreen
  // viewer strips the param so it always loads the full-size original.
  private _imageResizeDpr = 2;

  // Strip any existing size query param, leaving the canonical full-size URL.
  private _stripImageSizeParam(src: string): string {
    return src.replace(/[?&]w=\d+/, '').replace(/\?$/, '');
  }

  // Measure the image's current rendered width in CSS px (0 when not laid out).
  private _measureImageWidth(img: HTMLImageElement): number {
    const attrWidth = img.getAttribute('width');
    if (attrWidth && /^\d+$/.test(attrWidth)) {
      const w = parseInt(attrWidth, 10);
      if (w > 0) return w;
    }
    return img.offsetWidth > 0 ? img.offsetWidth : 0;
  }

  private _applyImageSizes(): void {
    if (!this._quillInstance) return;
    const images = this._quillInstance.root.querySelectorAll('img');
    // Rewriting src mutates the DOM, which Quill's MutationObserver surfaces as
    // a 'user' change. Guard it; reset via a macrotask (the observer is a microtask).
    this._isResizingImages = true;
    setTimeout(() => { this._isResizingImages = false; }, 0);
    for (const img of Array.from(images)) {
      // Native browser lazy loading: defer fetch until the image is near viewport.
      if (img.getAttribute('loading') !== 'lazy') {
        img.setAttribute('loading', 'lazy');
      }

      const src = img.getAttribute('src');
      if (!src) continue;
      const base = this._stripImageSizeParam(src);
      const width = this._measureImageWidth(img);
      if (width <= 0) continue; // not measurable yet; a `load` event will retry
      const w = Math.min(4096, Math.max(16, Math.round(width * this._imageResizeDpr)));
      const next = `${base}?w=${w}`;
      if (next !== img.getAttribute('src')) {
        img.setAttribute('src', next);
      }
    }
  }

  // Apply sizing on the next animation frame so layout (widths) has settled
  // after a content change. Coalesces rapid calls into a single pass.
  private _scheduleApplyImageSizes(): void {
    if (this._imageResizeRaf) return;
    this._imageResizeRaf = requestAnimationFrame(() => {
      this._imageResizeRaf = 0;
      this._applyImageSizes();
    });
  }

  // Delegated capture-phase image `load`: size the image now that it has
  // dimensions (covers images whose width is only known after load).
  private _onImageLoad = (e: Event): void => {
    const target = e.target as HTMLElement | null;
    if (target && target.tagName === 'IMG' && this._quillInstance?.root.contains(target)) {
      this._scheduleApplyImageSizes();
    }
  };

  /** Toolbar control groups for a layout. Mobile keeps only high-frequency
   *  controls on the row; the rest go into a "More" panel (see _layoutToolbar). */
  private _getToolbarGroups(isMobile: boolean): any[] {
    // `false` is Quill's idiom for a valueless (clear-format) option selected by default.
    const sizeOptions: any[] = ['10px', false, 'large', 'huge', '4.242px'];
    if (isMobile) {
      return [
        ['bold', 'italic', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
        ['link', 'image'],
        ['undo', 'redo'],
        [{ 'size': sizeOptions }, 'format-painter', 'blockquote', 'code-block', { 'color': [] }, { 'background': [] }, { 'script': 'super' }, { 'script': 'sub' }, { 'align': [] }, { [TableUp.toolName]: [] }, 'clean', 'copy-markdown', 'paste-markdown'],
      ];
    }
    return [
      [{ 'size': sizeOptions }],
      ['format-painter', 'bold', 'italic', 'strike', 'code-block', 'blockquote'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'super' }, { 'script': 'sub' }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
      [{ 'align': [] }],
      ['link', 'image'],
      [{ [TableUp.toolName]: [] }],
      ['clean', 'undo', 'redo'],
      ['copy-markdown', 'paste-markdown'],
    ];
  }

  /** Rebuild Quill when the window crosses the 860px breakpoint so the toolbar
   *  switches mobile/desktop layouts. The container div is reset in place (React
   *  owns the ref); content and selection are restored. */
  private _rebuildQuillForLayout(): void {
    if (!this._quillInstance || !this._canEdit) return;
    const mode = window.innerWidth <= 860 ? 'mobile' : 'desktop';
    if (mode === this._toolbarMode) return;

    const quill = this._quillInstance;
    const container = this._editorContainerRef.current;
    if (!container) return;

    const contents = quill.getContents();
    const range = quill.getSelection();

    quill.off('text-change', this._onTextChange);
    quill.off('selection-change', this._onSelectionChange);
    this._codeBlockObserver?.disconnect();
    this._codeBlockObserver = null;
    this._floatingCopyBtn?.remove();
    this._floatingCopyBtn = null;
    if (this._toolbarMoreOutsideHandler) {
      document.removeEventListener('click', this._toolbarMoreOutsideHandler);
      this._toolbarMoreOutsideHandler = null;
    }

    // Quill renders .ql-toolbar as a sibling of the container (both inside
    // #snow-container). Remove it so the fresh instance starts clean.
    container.closest('#snow-container')?.querySelectorAll('.ql-toolbar').forEach(el => el.remove());

    // Reset the container to its pre-Quill state so Quill can re-init into it.
    container.className = '';
    container.innerHTML = '';
    container.removeAttribute('style');

    this._quillInstance = null;
    this._initializeQuill();

    // Restore content and selection.
    const fresh = this._quillInstance as Quill | null;
    if (fresh) {
      fresh.setContents(contents);
      if (range) {
        try { fresh.setSelection(range, 'silent'); } catch (err) { console.debug('Could not restore selection after Quill rebuild:', err); }
      }
      this._scheduleApplyImageSizes();
    }
  }

  /** On mobile, collapse the low-frequency controls (last group) into a "More"
   *  dropdown. They move into a panel inside `.ql-toolbar` so Quill's delegated
   *  handlers and picker popups keep working. */
  private _layoutToolbar(): void {
    // .ql-toolbar is a sibling of the ref (both inside #snow-container), so scope
    // the lookup to the container, not the ref.
    const toolbar = this._editorContainerRef.current?.closest('#snow-container')?.querySelector('.ql-toolbar') as HTMLElement | null;
    if (!toolbar || window.innerWidth > 860) return;
    if (toolbar.dataset.mobileLaidOut === '1') return;
    toolbar.dataset.mobileLaidOut = '1';

    const groups = toolbar.querySelectorAll('.ql-formats');
    const moreGroup = groups[groups.length - 1];
    if (!moreGroup) return;

    const more = document.createElement('div');
    more.className = 'ql-more';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ql-more-btn';
    btn.setAttribute('aria-label', 'More formatting');
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>';

    const panel = document.createElement('div');
    panel.className = 'ql-more-panel';

    // Move the low-frequency controls into the panel (they stay inside .ql-toolbar).
    while (moreGroup.firstChild) {
      panel.appendChild(moreGroup.firstChild);
    }
    moreGroup.remove();
    more.appendChild(btn);
    more.appendChild(panel);
    toolbar.appendChild(more);

    btn.addEventListener('click', () => {
      const open = panel.classList.toggle('ql-more-panel-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    this._toolbarMoreOutsideHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && !target.closest('.ql-more')) {
        panel.classList.remove('ql-more-panel-open');
        btn.setAttribute('aria-expanded', 'false');
      }
    };
    document.addEventListener('click', this._toolbarMoreOutsideHandler);
  }

  // Percentage-width images reflow when the content column resizes, so re-request
  // the size (debounced ~200ms). Also rebuilds the toolbar at the 860px breakpoint.
  private _onWindowResize = (): void => {
    // Breakpoint check is cheap — run it immediately so the layout switch is instant.
    this._rebuildQuillForLayout();
    if (this._imageResizeThrottle) return;
    this._imageResizeThrottle = window.setTimeout(() => {
      this._imageResizeThrottle = 0;
      this._scheduleApplyImageSizes();
    }, 200);
  };

  // Called by the resize plugin when a drag ends (width attribute just changed).
  private _onImageResizeEnd = (): void => {
    this._scheduleApplyImageSizes();
  };

  // quill-table-up leaves its table overlays misaligned on window resize of the centered column; re-align them here.
  private _syncTableOverlays(): void {
    const quill = this._quillInstance;
    if (!quill) return;
    const root = quill.root;
    const toolbox = quill.container?.querySelector('.table-up-toolbox') as HTMLElement | null;
    if (toolbox) {
      toolbox.style.left = `${root.offsetLeft}px`;
      toolbox.style.top = `${root.offsetTop}px`;
    }
    const tableUp = quill.getModule(TableUp.moduleName) as {
      getModule: (name: string) => { update?: () => void; root?: HTMLElement } | undefined;
    } | undefined;
    if (!tableUp?.getModule) return;
    const box = tableUp.getModule('table-resize-box');
    if (box?.root?.classList.contains('is-hidden')) return;
    for (const name of ['table-resize-box', 'table-align', 'table-resize-scale']) {
      tableUp.getModule(name)?.update?.();
    }
  }

  private _observeTableLayout(): void {
    this._tableLayoutObserver?.disconnect();
    this._tableLayoutObserver = null;
    const target = this._quillInstance?.container ?? this._quillInstance?.root;
    if (!target || typeof ResizeObserver === 'undefined') return;
    this._tableLayoutObserver = new ResizeObserver(() => {
      if (this._tableSyncThrottle) return;
      this._tableSyncThrottle = window.setTimeout(() => {
        this._tableSyncThrottle = 0;
        this._syncTableOverlays();
      }, 80);
    });
    this._tableLayoutObserver.observe(target);
  }

  @action.bound
  private _onEditorKeyDown(e: React.KeyboardEvent<HTMLDivElement>){
    if (e.key === 'Escape' && this._isPainting) {
      this._isPainting = false;
      this._savedFormats = null;
      return;
    }
    if (e.ctrlKey || e.metaKey) {
      switch (e.keyCode) {
        case 115: // ctrl+s
        case 83: // ctrl+S
          e.preventDefault();
          this._saveChanges();

          break;
      }
    }
  }

  @action.bound
  private _onBeforeUnload() {
    if (this._changes.length() > 0) {
      return 'There are unsaved changes. Are you sure you want to leave?';
    }
    return void 0;
  }

  private _saveChanges(){
    // socket.io drops events while the socket is down, so keep _changes until online.
    if (!onlineService.isOnline) return;

    this._savingLoader.subscribe(new Promise<void>(resolve => window.setTimeout(resolve, 1500)));

    socketClientService.emit('note-changed', {
      noteId: this.props.noteId,
      delta: JSON.stringify(this._changes),
      fullDelta: JSON.stringify(this._quillInstance?.getContents())
    });

    this._changes = new Delta();
  }

  @action.bound
  private _saveCurrentChanges() {
    if (this._changes.length() > 0) {
      this._saveChanges();
    }
  }

  @action.bound
  private _onTextChange(delta: DeltaType, _oldDelta: DeltaType, source: EmitterSource){
    if (this._isUpdatingFromServer || !this._isLoaded || this._isResizingImages || source !== 'user') return;
    this._changes = this._changes.compose(delta);
  }

  @action.bound
  private _onSelectionChange(range: { index: number; length: number } | null, _oldRange: unknown, source: EmitterSource) {
    if (!this._isPainting || !range || source !== 'user') return;
    if (!this._quillInstance || !this._savedFormats) return;

    let applyRange = range;

    // Click without drag-selection → expand to word boundaries
    if (range.length === 0) {
      const text = this._quillInstance.getText();
      const wordChar = /\w/;
      let start = range.index;
      let end = range.index;
      while (start > 0 && wordChar.test(text[start - 1])) start--;
      while (end < text.length && wordChar.test(text[end])) end++;
      if (end === start) return; // cursor not on a word – do nothing
      applyRange = { index: start, length: end - start };
    }

    // Block-level formats that must never be cleared or overwritten (list, table rows, etc.)
    const BLOCK_FORMATS = new Set(['align', 'direction', 'indent', 'list', 'blockquote', 'code-block', 'header',
      'table', 'td', 'tr', 'table-col', 'table-cell-line']);

    // Get formats currently at the target so we can clear inline ones that aren't in the source
    const currentFormats = this._quillInstance.getFormat(applyRange.index, applyRange.length) as Record<string, unknown>;

    const toApply: Record<string, unknown> = {};

    // Nullify existing inline formats at target (to clear stale formatting)
    for (const key of Object.keys(currentFormats)) {
      if (!BLOCK_FORMATS.has(key)) {
        toApply[key] = false;
      }
    }

    // Apply saved inline formats (overrides the false values above where keys overlap)
    for (const [key, val] of Object.entries(this._savedFormats)) {
      if (!BLOCK_FORMATS.has(key)) {
        toApply[key] = val;
      }
    }

    this._quillInstance.formatText(applyRange.index, applyRange.length, toApply, 'user');

    this._isPainting = false;
    this._savedFormats = null;
  }

  // Click-to-reveal for spoiler text (.ql-size-4.242px): toggles a pure CSS class
  // (.revealed). No preventDefault (keeps caret/selection); capture phase so it
  // fires before Quill handlers. Works in edit and read-only views.
  private _setupSpoilerReveal(): void {
    if (!this._editorContainerRef.current) return;
    const editorEl = this._editorContainerRef.current;

    editorEl.addEventListener('click', (e) => {
      const span = (e.target as Element).closest<HTMLElement>('.ql-size-4\\.242px');
      if (!span) return;
      // If the user just dragged to select text, keep it revealed so they can
      // copy it — don't treat that click as a toggle.
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) return;
      span.classList.toggle('revealed');
    }, true);
  }

  private _setupCodeBlockFeatures(): void {
    if (!this._editorContainerRef.current) return;
    const editorEl = this._editorContainerRef.current;

    const copyIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
    const checkIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;

    // ── Floating copy button ──────────────────────────────────────────────────
    // Lives on document.body (outside ql-editor) so Quill never strips it.
    const btn = document.createElement('button');
    btn.className = 'ql-copy-btn';
    btn.title = 'Copy code';
    btn.innerHTML = copyIconSvg;
    document.body.appendChild(btn);
    this._floatingCopyBtn = btn;

    const showBtn = (container: HTMLElement) => {
      this._floatingCopyTarget = container;
      const select = container.querySelector<HTMLElement>('select.ql-ui');
      const anchor = select ?? container;
      const rect = anchor.getBoundingClientRect();
      btn.style.top = `${rect.top + (rect.height - 26) / 2}px`;
      btn.style.left = `${rect.left - 28}px`;
      btn.style.right = '';
      btn.style.display = 'flex';
    };

    const hideBtn = () => {
      btn.style.display = 'none';
      this._floatingCopyTarget = null;
    };

    btn.addEventListener('mousedown', (e) => {
      e.preventDefault();
      const target = this._floatingCopyTarget;
      if (!target) return;
      const codeText = Array.from(target.querySelectorAll('.ql-code-block'))
        .map(b => (b as HTMLElement).innerText)
        .join('\n');
      navigator.clipboard.writeText(codeText).then(() => {
        btn.innerHTML = checkIconSvg;
        btn.classList.add('ql-copy-btn--copied');
        setTimeout(() => {
          btn.innerHTML = copyIconSvg;
          btn.classList.remove('ql-copy-btn--copied');
        }, 1500);
      }).catch(() => { /* clipboard unavailable */ });
    });

    btn.addEventListener('mouseleave', (e) => {
      const related = e.relatedTarget as Element | null;
      if (!related?.closest('.ql-code-block-container')) hideBtn();
    });

    editorEl.addEventListener('mouseover', (e) => {
      const container = (e.target as Element).closest<HTMLElement>('.ql-code-block-container');
      if (container) {
        showBtn(container);
      } else if (!btn.matches(':hover')) {
        hideBtn();
      }
    });

    editorEl.addEventListener('mouseleave', (e) => {
      const related = e.relatedTarget as Node | null;
      if (!related || !btn.contains(related)) hideBtn();
    });

    // Reposition while editor scrolls
    editorEl.querySelector('.ql-editor')?.addEventListener('scroll', () => {
      if (this._floatingCopyTarget) showBtn(this._floatingCopyTarget);
    });

    // ── Spellcheck (no DOM insertions) ───────────────────────────────────────
    const applySpellcheck = (container: HTMLElement) => {
      const firstBlock = container.querySelector<HTMLElement>('.ql-code-block');
      const lang = firstBlock?.dataset.language ?? 'plaintext';
      container.setAttribute('spellcheck', lang === 'plaintext' ? 'true' : 'false');
    };

    editorEl.querySelectorAll<HTMLElement>('.ql-code-block-container').forEach(applySpellcheck);

    // Capture-phase change listener covers all <select class="ql-ui"> within editor.
    editorEl.addEventListener('change', (e) => {
      const sel = e.target as HTMLElement;
      if (sel.tagName === 'SELECT' && sel.classList.contains('ql-ui')) {
        const container = sel.closest<HTMLElement>('.ql-code-block-container');
        if (container) {
          setTimeout(() => applySpellcheck(container), 0); // wait for language change to apply
        }
      }
    }, true);

    this._codeBlockObserver = new MutationObserver((mutations) => {
      const containers = new Set<HTMLElement>();
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of Array.from(mutation.addedNodes)) {
            if (!(node instanceof Element)) continue;
            if (node.classList.contains('ql-code-block-container')) {
              containers.add(node as HTMLElement);
            } else {
              node.querySelectorAll<HTMLElement>('.ql-code-block-container').forEach(c => containers.add(c));
            }
          }
        }
        if (mutation.type === 'attributes' && mutation.target instanceof Element) {
          const container = mutation.target.closest<HTMLElement>('.ql-code-block-container');
          if (container) containers.add(container);
        }
      }
      containers.forEach(applySpellcheck);
    });

    this._codeBlockObserver.observe(editorEl, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-language'],
    });
  }

  private async _copyAsMarkdown(): Promise<void> {
    if (!this._quillInstance) return;
    const html = (this._quillInstance as any).getSemanticHTML() as string;
    const td = new TurndownService({ headingStyle: 'atx', codeBlockStyle: 'fenced' });
    td.addRule('strikethrough', {
      filter: ['del', 's'],
      replacement: (content: string) => `~~${content}~~`,
    });
    const markdown = td.turndown(html);
    try {
      await navigator.clipboard.writeText(markdown);
      notificationService.success('Copied as Markdown');
    } catch {
      notificationService.error('Could not copy to clipboard.');
    }
  }

  private async _pasteMarkdown(): Promise<void> {
    if (!this._quillInstance) return;
    try {
      const text = await navigator.clipboard.readText();
      const html = marked.parse(text) as string;
      const delta = (this._quillInstance.clipboard as any).convert({ html });
      const range = this._quillInstance.getSelection(true);
      this._quillInstance.updateContents(
        new Delta().retain(range.index).delete(range.length).concat(delta),
        'user'
      );
      this._quillInstance.setSelection(range.index + delta.length() - 1, 0, 'silent');
    } catch {
      notificationService.error('Could not read from clipboard.');
    }
  }

  @action.bound
  private _onNoteUpdate(data: any){
    this._updateFromData(JSON.parse(data.delta), true, "api");
  }

  private async _uploadImage(file: File): Promise<string> {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const comma = dataUrl.indexOf(',');
        resolve(dataUrl.substring(comma + 1));
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const uploadPromise = apiService.post<{ url: string }>(
      `/api/note/${this.props.noteId}/image`,
      { data: base64, contentType: file.type },
      { defaultErrorHandling: false }
    );
    this._uploadLoader.subscribe(uploadPromise);

    try {
      const result = await uploadPromise;
      return result.url;
    } catch (error) {
      const httpError = error as HttpError;
      const msg = httpError?.code === 413
        ? 'Image is too large to upload (max 20 MB).'
        : `Failed to upload image: ${httpError?.message ?? 'Unknown error'}`;
      notificationService.error(msg);
      throw error;
    }
  }

  @action.bound
  private _onSocketAccessDenied() {
    notificationService.error('Access to this note was denied.');
  }

  @action.bound
  private _onSocketNotFound() {
    notificationService.error('Note not found on server.');
  }

  @action.bound
  private _onSocketSaveError(data: { message?: string }) {
    notificationService.error(`Note could not be saved: ${data?.message ?? 'server error'}`);
  }
}